import { IconAlertTriangle, IconPlus } from '@tabler/icons-react';
import type { BlockVolume } from '@xver/apis/v1/block-storage/model';
import { Button, CopyButton } from '@xver/components/Atoms';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';
import { DataTable, DataTableColumn } from '@xver/components/Organisms';
import { useActiveProjectScope, useProductAccess } from '@xver/hooks/useActiveProject';
import { guestUsageSnippets } from '@xver/utils/blockStorage';
import { focusAfterRender } from '@xver/utils/projects';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatBaht } from '../../ContainerEngineInstancePage/utils/volumeCost';
import { useBlockVolumesQuery } from '../hooks/useBlockVolumesQuery';
import { useVolumeStatusAnnouncement } from '../hooks/useVolumeStatusAnnouncement';
import AttachVolumeModal, { type AttachTarget } from './AttachVolumeModal';
import DetachVolumeModal from './DetachVolumeModal';
import VolumeRowActions from './VolumeRowActions';
import VolumeStatusBadge from './VolumeStatusBadge';
import VolumesLoadError from './VolumesLoadError';

const DETACH_ONLY = ['detach'] as const;

interface AttachedVolumesPanelProps {
    target: AttachTarget;
    /** The target's project; volumes are listed and picked from it. Omit to use the active project. */
    projectId?: string;
    /** Manage access to the VM or container itself; attach and detach also need it. */
    canManageTarget: boolean;
}

/**
 * Block storage volumes attached to one VM or container, with attach and detach. Render it only
 * while the caller has at least view access to Block storage.
 */
export default function AttachedVolumesPanel({
    target,
    projectId,
    canManageTarget,
}: AttachedVolumesPanelProps) {
    const { t } = useTranslation('blockStorage');
    const access = useProductAccess('block_storage');
    const { activeProjectId } = useActiveProjectScope();
    const isVm = target.type === 'vm';
    const inScope = !projectId || activeProjectId === null || activeProjectId === projectId;

    const { data, isPending, isError, refetch } = useBlockVolumesQuery({
        params: isVm ? { instanceId: target.instanceId } : { containerId: target.containerId },
        projectId,
        enabled: inScope,
    });
    const volumes = data?.data ?? [];
    const announcement = useVolumeStatusAnnouncement(data?.data);
    const attachButtonId = useId();
    const retryButtonId = useId();
    const focusTarget = () =>
        document.getElementById(attachButtonId) ?? document.getElementById('main-content');
    const canAttach = canManageTarget && access === 'manage';
    const canDetach = (volume: BlockVolume) => canManageTarget && volume.access === 'manage';

    const [attachOpened, setAttachOpened] = useState(false);
    const [detachTarget, setDetachTarget] = useState<BlockVolume | null>(null);

    const pendingReboot = volumes.filter(
        (volume) => volume.attachment?.type === 'vm' && volume.attachment.pendingReboot
    );

    const locationColumn: DataTableColumn<BlockVolume> = isVm
        ? {
              key: 'device',
              label: t('table.devicePath'),
              render: (volume) =>
                  volume.attachment?.type === 'vm' ? (
                      <div className="flex flex-row items-center gap-2">
                          <code className="font-mono text-xs break-all">
                              {volume.attachment.devicePath}
                          </code>
                          <CopyButton
                              value={volume.attachment.devicePath}
                              ariaLabel={t('copyDevicePath', { name: volume.name })}
                          />
                      </div>
                  ) : (
                      '-'
                  ),
          }
        : {
              key: 'mountPath',
              label: t('table.mountPath'),
              render: (volume) =>
                  volume.attachment?.type === 'container' ? (
                      <code className="font-mono text-xs break-all">
                          {volume.attachment.mountPath}
                      </code>
                  ) : (
                      '-'
                  ),
          };

    const columns: DataTableColumn<BlockVolume>[] = [
        {
            key: 'name',
            label: t('table.name'),
            render: (volume) => (
                <span className="font-medium text-fg text-sm break-all">{volume.name}</span>
            ),
        },
        {
            key: 'size',
            label: t('table.size'),
            render: (volume) => (
                <span className="font-mono text-sm text-nowrap">
                    {t('sizeValue', { size: volume.sizeGiB })}
                </span>
            ),
        },
        {
            key: 'status',
            label: t('table.status'),
            render: (volume) => <VolumeStatusBadge volume={volume} />,
        },
        locationColumn,
        {
            key: 'cost',
            label: t('table.monthlyCost'),
            align: 'right',
            render: (volume) => (
                <span className="font-mono text-sm text-nowrap">
                    {t('perMonth', { amount: formatBaht(volume.monthlyPrice) })}
                </span>
            ),
        },
    ];

    return (
        <>
            <DialogBox
                label={t('panel.title')}
                description={t(isVm ? 'panel.descriptionVm' : 'panel.descriptionContainer')}
                rightDescription={
                    canAttach && (
                        <Button
                            id={attachButtonId}
                            size="sm"
                            variant="secondary"
                            leftIcon={<IconPlus size={14} aria-hidden="true" />}
                            onClick={() => setAttachOpened(true)}
                        >
                            {t('panel.attach')}
                        </Button>
                    )
                }
            >
                <div className="w-full flex flex-col gap-3 p-3">
                    <p role="status" className="sr-only">
                        {announcement}
                    </p>
                    {pendingReboot.length > 0 && (
                        <div className="w-full flex flex-row items-start gap-2 p-3 rounded-md border border-amber-500/40 bg-amber-500/10">
                            <IconAlertTriangle
                                size={18}
                                className="shrink-0 text-amber-400 light:text-amber-800"
                                aria-hidden="true"
                            />
                            <p className="text-sm text-amber-200/90 light:text-amber-800">
                                {t('panel.pendingReboot', {
                                    names: pendingReboot.map((volume) => volume.name).join(', '),
                                })}
                            </p>
                        </div>
                    )}
                    {isError && !data ? (
                        <VolumesLoadError
                            retryButtonId={retryButtonId}
                            onRetry={async () => {
                                const result = await refetch();
                                focusAfterRender(() =>
                                    result.isError
                                        ? document.getElementById(retryButtonId)
                                        : focusTarget()
                                );
                            }}
                        />
                    ) : (
                        <DataTable
                            caption={t('panel.title')}
                            columns={columns}
                            rows={volumes}
                            isLoading={isPending}
                            rowKey={(volume) => volume.id}
                            emptyMessage={t(isVm ? 'panel.emptyVm' : 'panel.emptyContainer')}
                            actionsLabel={t('table.actions')}
                            actions={
                                volumes.some(canDetach)
                                    ? (volume) =>
                                          canDetach(volume) ? (
                                              <VolumeRowActions
                                                  volume={volume}
                                                  actions={DETACH_ONLY}
                                                  onAction={(_, picked) => setDetachTarget(picked)}
                                              />
                                          ) : null
                                    : undefined
                            }
                        />
                    )}
                    {isVm ? (
                        volumes.map((volume) =>
                            volume.attachment?.type === 'vm' ? (
                                <GuestUsage
                                    key={volume.id}
                                    name={volume.name}
                                    devicePath={volume.attachment.devicePath}
                                />
                            ) : null
                        )
                    ) : (
                        <p className="text-sm text-fg/68">{t('panel.containerFormatNote')}</p>
                    )}
                    <p className="text-sm text-fg/68">{t('portability')}</p>
                </div>
            </DialogBox>

            <AttachVolumeModal
                opened={attachOpened}
                onClose={() => setAttachOpened(false)}
                target={target}
                projectId={projectId}
            />
            <DetachVolumeModal
                volume={detachTarget}
                onClose={() => setDetachTarget(null)}
                onDetached={() => focusAfterRender(focusTarget)}
            />
        </>
    );
}

function GuestUsage({ name, devicePath }: { name: string; devicePath: string }) {
    const { t } = useTranslation('blockStorage');
    const snippets = guestUsageSnippets(devicePath, name);
    const steps = [
        ['format', snippets.format],
        ['mount', snippets.mount],
        ['grow', snippets.grow],
        ['unmount', snippets.unmount],
    ] as const;

    return (
        <details className="w-full rounded-md border-[0.5px] border-line bg-surface-2">
            <summary className="min-h-10 px-3 py-2 cursor-pointer text-sm font-medium">
                {t('guest.summary', { name })}
            </summary>
            <ol className="flex flex-col gap-3 px-3 pb-3">
                {steps.map(([step, command]) => (
                    <li key={step} className="flex flex-col gap-1">
                        <span className="text-sm text-fg/78">{t(`guest.${step}`)}</span>
                        <div className="flex flex-row items-start gap-2">
                            <code className="flex-1 px-2 py-1 rounded bg-fg/5 font-mono text-xs break-all">
                                {command}
                            </code>
                            <CopyButton
                                value={command}
                                ariaLabel={t('guest.copy', { step: t(`guest.${step}`) })}
                            />
                        </div>
                    </li>
                ))}
            </ol>
        </details>
    );
}
